(function () {
  'use strict';

  /**
   * Multilingual-safe Shopify route root.
   *
   * Examples:
   * Default language: /
   * Italian language: /it/
   */
  function getRouteRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : root + '/';
  }

  function shopifyRoute(path) {
    return getRouteRoot() + String(path || '').replace(/^\/+/, '');
  }

  /**
   * Selling plan IDs returned by the Hengam app.
   *
   * Special Order: 691469713757
   * Pre-order:     691429638493
   */
  const SPECIAL_ORDER_SELLING_PLAN_IDS = ['691469713757'];
  const PREORDER_SELLING_PLAN_IDS = ['691429638493'];

  const ALL_SELLING_PLAN_IDS = [
    ...SPECIAL_ORDER_SELLING_PLAN_IDS,
    ...PREORDER_SELLING_PLAN_IDS
  ];

  /**
   * Keep the shared Order now button hidden briefly while the Hengam app
   * finishes inserting or updating its hidden selling_plan input.
   *
   * This prevents a normal Order now button from flashing before the form
   * is identified as Special Order or Pre-order.
   */
  const ORDER_NOW_REVEAL_DELAY = 900;

  let ticking = false;
  let delayedApplyTimer = null;

  function getLocale() {
    const shopifyLocale = String(window.Shopify?.locale || '').trim();

    if (shopifyLocale) {
      return shopifyLocale.toLowerCase().split('-')[0];
    }

    const documentLocale = String(document.documentElement.lang || '').trim();

    if (documentLocale) {
      return documentLocale.toLowerCase().split('-')[0];
    }

    return window.location.pathname.startsWith('/it/') ? 'it' : 'en';
  }

  function getDefaultLabel(group, key, fallback) {
    const locale = getLocale();

    const defaults = {
      en: {
        specialOrder: {
          buttonText: 'Special Order now',
          badgeText: 'Special Order',
          purchaseType: 'Special order',
          availabilityDate: '2/4 weeks',
          payment: 'Full payment at checkout',
          orderMessage:
            'If your order is on Special Order it will take between 1/4 weeks after your order and our confirmation.',
          orderType: 'Special Order'
        },
        preorder: {
          buttonText: 'Pre-order now',
          badgeText: 'Pre-order',
          purchaseType: 'Preorder',
          availabilityDate: '6 months',
          payment: 'Partial payment at checkout',
          orderMessage: '',
          orderType: 'Pre-order'
        }
      },
      it: {
        specialOrder: {
          buttonText: 'Acquista ora',
          badgeText: 'Ordine speciale',
          purchaseType: 'Ordine speciale',
          availabilityDate: '2/4 settimane',
          payment: 'Pagamento completo al checkout',
          orderMessage:
            'Se il tuo ordine è uno Special Order, ci vorranno da 1 a 4 settimane dal momento in cui effettui l\'ordine e ricevi la nostra conferma.',
          orderType: 'Special Order'
        },
        preorder: {
          buttonText: 'Preordinalo ora',
          badgeText: 'Preordine',
          purchaseType: 'Preordine',
          availabilityDate: '6 mesi',
          payment: 'Pagamento parziale al checkout',
          orderMessage: '',
          orderType: 'Pre-order'
        }
      }
    };

    return defaults?.[locale]?.[group]?.[key] || defaults?.en?.[group]?.[key] || fallback;
  }

  function getLabel(group, key, fallback) {
    const customValue = window.sellingPlanLabels?.[group]?.[key];

    /**
     * Do not accept a preorder-style translation for the Special Order
     * button. The Hengam Italian configuration currently contains
     * "Preordinalo ora" for the Special Order campaign.
     */
    if (
      group === 'specialOrder' &&
      key === 'buttonText' &&
      /pre[-\s]?ordin/i.test(String(customValue || ''))
    ) {
      return getDefaultLabel(group, key, fallback);
    }

    return customValue || getDefaultLabel(group, key, fallback);
  }

  function getThemeString(key, fallback) {
    return window.themeStrings?.[key] || fallback;
  }

  function createSpecialOrderConfig() {
    return {
      buttonText: getLabel('specialOrder', 'buttonText', 'Special Order now'),
      badgeText: getLabel('specialOrder', 'badgeText', 'Special Order'),
      purchaseType: getLabel('specialOrder', 'purchaseType', 'Special order'),
      availabilityDate: getLabel('specialOrder', 'availabilityDate', '2/4 weeks'),
      payment: getLabel('specialOrder', 'payment', 'Full payment at checkout'),
      orderMessage: getLabel(
        'specialOrder',
        'orderMessage',
        'If your order is on Special Order it will take between 1/4 weeks after your order and our confirmation.'
      ),
      orderType: getLabel('specialOrder', 'orderType', 'Special Order')
    };
  }

  function createPreorderConfig() {
    return {
      buttonText: getLabel('preorder', 'buttonText', 'Pre-order now'),
      badgeText: getLabel('preorder', 'badgeText', 'Pre-order'),
      purchaseType: getLabel('preorder', 'purchaseType', 'Preorder'),
      availabilityDate: getLabel('preorder', 'availabilityDate', '6 months'),
      payment: getLabel('preorder', 'payment', 'Partial payment at checkout'),
      orderMessage: getLabel('preorder', 'orderMessage', ''),
      orderType: getLabel('preorder', 'orderType', 'Pre-order')
    };
  }

  /**
   * The app places the active selling plan in an input or select inside the
   * active product form. This is the source used to decide whether the
   * purchase is Special Order, Pre-order, or a normal order.
   */
  function getHiddenSellingPlanId(form) {
    if (!form) return '';

    const controls = Array.from(
      form.querySelectorAll(
        'input[name="selling_plan"], select[name="selling_plan"]'
      )
    ).filter(function (control) {
      return !control.disabled && String(control.value || '').trim();
    });

    const activeControl = controls[controls.length - 1];

    return activeControl ? String(activeControl.value || '').trim() : '';
  }

  function resolveSellingPlanState(form) {
    const sellingPlanId = getHiddenSellingPlanId(form);

    if (SPECIAL_ORDER_SELLING_PLAN_IDS.includes(sellingPlanId)) {
      return {
        mode: 'special_order',
        sellingPlanId: sellingPlanId,
        config: createSpecialOrderConfig()
      };
    }

    if (PREORDER_SELLING_PLAN_IDS.includes(sellingPlanId)) {
      return {
        mode: 'preorder',
        sellingPlanId: sellingPlanId,
        config: createPreorderConfig()
      };
    }

    return null;
  }

  function updateOrCreateHiddenInput(form, name, value) {
    if (!form || !name) return null;

    let input = form.querySelector('input[name="' + name + '"]');
    const cleanValue = value == null ? '' : String(value);

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }

    if (input.value !== cleanValue) {
      input.value = cleanValue;
      input.setAttribute('value', cleanValue);
    }

    return input;
  }

  function removeHiddenInput(form, name) {
    if (!form || !name) return;

    form.querySelectorAll('input[name="' + name + '"]').forEach(function (input) {
      input.remove();
    });
  }

  function getProductIdFromForm(form) {
    if (!form) return '';

    const productForm = form.closest('product-form-component');

    if (productForm?.dataset?.productId) {
      return String(productForm.dataset.productId || '').trim();
    }

    const productIdInput = form.querySelector('input[name="product-id"]');

    return productIdInput ? String(productIdInput.value || '').trim() : '';
  }

  function getCurrentVariantId(form) {
    if (!form) return '';

    const variantInput = form.querySelector('input[name="id"]');

    return variantInput ? String(variantInput.value || '').trim() : '';
  }

  function getVariantInventoryData(form) {
    const productId = getProductIdFromForm(form);
    const variantId = getCurrentVariantId(form);

    if (!productId || !variantId) return null;

    const dataId = 'availability-variant-data-test-' + productId;
    const dataEl =
      form.querySelector('#' + dataId) ||
      document.getElementById(dataId);

    if (!dataEl) return null;

    try {
      const variantData = JSON.parse(dataEl.textContent || '{}');
      return variantData[String(variantId)] || null;
    } catch (error) {
      console.warn('[Selling Plan Overrides] Variant data JSON error:', error);
      return null;
    }
  }

  function isSoldOutDeny(form) {
    const data = getVariantInventoryData(form);

    if (!data) return false;

    return (
      data.available === false &&
      String(data.inventory_policy || '') === 'deny' &&
      Number(data.inventory_quantity || 0) <= 0
    );
  }

  function getOrderNowButtons(form) {
    if (!form) return [];

    return Array.from(form.querySelectorAll('[data-order-now-button]'));
  }

  function setOrderNowButtonVisibility(form, visible) {
    getOrderNowButtons(form).forEach(function (button) {
      button.hidden = !visible;
      button.disabled = !visible;
      button.setAttribute('aria-hidden', visible ? 'false' : 'true');

      if (visible) {
        button.removeAttribute('aria-busy');
      }
    });
  }

  function isVariantAvailableForOrderNow(form) {
    const data = getVariantInventoryData(form);

    if (data) {
      return data.available === true;
    }

    const variantInput = form?.querySelector('input[name="id"]');
    const addToCartBtn = form?.querySelector('button[name="add"]');

    if (!variantInput || variantInput.disabled) return false;

    return Boolean(addToCartBtn && !addToCartBtn.disabled);
  }

  function isOrderNowEligible(form) {
    if (!form) return false;

    const variantId = getCurrentVariantId(form);

    if (!variantId) return false;

    /*
     * Order now is reserved for a normal purchase. Any active selling plan
     * means the variant is not eligible, including the Hengam Special Order
     * and Pre-order plans.
     */
    if (getHiddenSellingPlanId(form)) return false;

    return isVariantAvailableForOrderNow(form);
  }

  function clearOrderNowReveal(form) {
    if (!form) return;

    if (form._orderNowRevealTimer) {
      clearTimeout(form._orderNowRevealTimer);
      form._orderNowRevealTimer = null;
    }

    delete form.dataset.orderNowPendingKey;
    delete form.dataset.orderNowVisibleKey;
  }

  function updateOrderNowButton(form) {
    const buttons = getOrderNowButtons(form);

    if (buttons.length === 0) return;

    const variantId = getCurrentVariantId(form);
    const sellingPlanId = getHiddenSellingPlanId(form);
    const stateKey = variantId + ':' + sellingPlanId;

    if (!isOrderNowEligible(form)) {
      clearOrderNowReveal(form);
      setOrderNowButtonVisibility(form, false);
      return;
    }

    if (form.dataset.orderNowVisibleKey === stateKey) {
      setOrderNowButtonVisibility(form, true);
      return;
    }

    if (
      form.dataset.orderNowPendingKey === stateKey &&
      form._orderNowRevealTimer
    ) {
      return;
    }

    clearOrderNowReveal(form);
    setOrderNowButtonVisibility(form, false);
    form.dataset.orderNowPendingKey = stateKey;

    form._orderNowRevealTimer = setTimeout(function () {
      form._orderNowRevealTimer = null;

      const currentVariantId = getCurrentVariantId(form);
      const currentSellingPlanId = getHiddenSellingPlanId(form);
      const currentStateKey = currentVariantId + ':' + currentSellingPlanId;

      if (
        currentStateKey !== stateKey ||
        !isOrderNowEligible(form)
      ) {
        delete form.dataset.orderNowPendingKey;
        setOrderNowButtonVisibility(form, false);
        return;
      }

      delete form.dataset.orderNowPendingKey;
      form.dataset.orderNowVisibleKey = stateKey;
      setOrderNowButtonVisibility(form, true);
    }, ORDER_NOW_REVEAL_DELAY);
  }

  function setButtonText(form, text) {
    if (!form || !text) return;

    const contentElements = form.querySelectorAll(
      '.add-to-cart-text__content'
    );

    contentElements.forEach(function (contentEl) {
      const nestedText = contentEl.querySelector('span span');
      const target = nestedText || contentEl;

      if (target.textContent.trim() !== text) {
        target.textContent = text;
      }
    });
  }

  function cleanupSellingPlanProperties(form) {
    removeHiddenInput(form, 'properties[Order Type]');
    removeHiddenInput(form, 'properties[Availability date]');
    removeHiddenInput(form, 'properties[Payment]');
    removeHiddenInput(form, 'properties[Order Message]');
  }

  function cleanupOldPurchaseTypeInput(form) {
    if (!form) return;

    removeHiddenInput(form, 'properties[_purchase_type]');
  }

  function updateBadge(form, badgeText) {
    if (!form || !badgeText) return;

    const productId = getProductIdFromForm(form);

    if (!productId) return;

    const productEl = document.querySelector(
      'product-price[data-product-id="' + productId + '"]'
    );

    if (!productEl) return;

    const badge = productEl.querySelector('.hengam-preorder-badge');

    if (badge && badge.textContent.trim() !== badgeText) {
      badge.textContent = badgeText;
    }
  }

  function applySellingPlanProperties(form, config) {
    if (!form || !config) return;

    const oldPurchaseTypeInput = form.querySelector(
      'input[name="properties[_purchase_type]"]'
    );

    if (oldPurchaseTypeInput) {
      oldPurchaseTypeInput.name = 'properties[Order Type]';
      oldPurchaseTypeInput.value = config.purchaseType;
      oldPurchaseTypeInput.setAttribute('value', config.purchaseType);
    }

    updateOrCreateHiddenInput(
      form,
      'properties[Order Type]',
      config.orderType || config.purchaseType
    );
    updateOrCreateHiddenInput(
      form,
      'properties[Availability date]',
      config.availabilityDate
    );
    updateOrCreateHiddenInput(form, 'properties[Payment]', config.payment);

    if (config.orderMessage) {
      updateOrCreateHiddenInput(
        form,
        'properties[Order Message]',
        config.orderMessage
      );
    } else {
      removeHiddenInput(form, 'properties[Order Message]');
    }
  }

  /**
   * Hengam's Italian Special Order configuration currently contains
   * btn_text: "Preordinalo ora". Patch the app's active page configuration
   * using the hidden selling-plan ID so its own rerenders use the right text.
   */
  function patchHengamPageConfig(state) {
    if (!state) return;

    try {
      if (typeof metafieldPreOrderData === 'undefined' || !metafieldPreOrderData) {
        return;
      }

      const locale = getLocale();
      const uiContents =
        metafieldPreOrderData?.product_page?.ui_contents?.[locale];

      if (!uiContents) return;

      uiContents.btn_text = state.config.buttonText;
      uiContents.badge_text = state.config.badgeText;
    } catch (error) {
      console.warn(
        '[Selling Plan Overrides] Could not patch Hengam page labels:',
        error
      );
    }
  }

  function applyOverrides() {
    document
      .querySelectorAll('form[data-type="add-to-cart-form"]')
      .forEach(function (form) {
        if (isSoldOutDeny(form)) {
          const addToCartBtn = form.querySelector('button[name="add"]');

          if (addToCartBtn) {
            addToCartBtn.classList.remove('hengam-preorder-button');
            addToCartBtn.disabled = true;
            delete addToCartBtn.dataset.orderMode;
          }

          setButtonText(form, getThemeString('soldOut', 'Sold out'));
          cleanupOldPurchaseTypeInput(form);
          cleanupSellingPlanProperties(form);
          updateOrderNowButton(form);

          return;
        }

        const state = resolveSellingPlanState(form);
        const addToCartBtn = form.querySelector('button[name="add"]');

        if (!state) {
          if (addToCartBtn) {
            delete addToCartBtn.dataset.orderMode;
          }

          cleanupOldPurchaseTypeInput(form);
          cleanupSellingPlanProperties(form);
          updateOrderNowButton(form);
          return;
        }

        patchHengamPageConfig(state);

        if (addToCartBtn) {
          addToCartBtn.dataset.orderMode = state.mode;
        }

        setButtonText(form, state.config.buttonText);
        updateBadge(form, state.config.badgeText);
        applySellingPlanProperties(form, state.config);
        updateOrderNowButton(form);
      });
  }

  function requestApplyOverrides() {
    if (ticking) return;

    ticking = true;

    requestAnimationFrame(function () {
      applyOverrides();
      ticking = false;
    });
  }

  function requestDelayedApply() {
    requestApplyOverrides();
    clearTimeout(delayedApplyTimer);

    delayedApplyTimer = setTimeout(function () {
      requestApplyOverrides();
    }, 80);
  }

  function bindFormObservers() {
    document
      .querySelectorAll('form[data-type="add-to-cart-form"]')
      .forEach(function (form) {
        if (form.dataset.sellingPlanOverrideObserverBound === 'true') return;

        form.dataset.sellingPlanOverrideObserverBound = 'true';

        const observer = new MutationObserver(function (mutations) {
          const shouldRun = mutations.some(function (mutation) {
            if (mutation.type === 'characterData') {
              return Boolean(
                mutation.target.parentElement?.closest(
                  '.add-to-cart-text__content, .hengam-preorder-badge'
                )
              );
            }

            if (mutation.type === 'attributes') {
              return (
                mutation.target?.matches?.(
                  'input[name="selling_plan"], select[name="selling_plan"], input[name="id"]'
                ) ||
                mutation.target?.closest?.('.add-to-cart-text__content')
              );
            }

            if (mutation.type === 'childList') {
              const changedNodes = [
                ...Array.from(mutation.addedNodes || []),
                ...Array.from(mutation.removedNodes || [])
              ];

              return (
                mutation.target?.closest?.(
                  '.add-to-cart-text__content, button[name="add"], [data-order-now-button]'
                ) ||
                changedNodes.some(function (node) {
                  return (
                    node instanceof HTMLElement &&
                    (node.matches?.(
                      'input[name="selling_plan"], select[name="selling_plan"], button[name="add"], [data-order-now-button]'
                    ) ||
                      node.querySelector?.(
                        'input[name="selling_plan"], select[name="selling_plan"], button[name="add"], [data-order-now-button]'
                      ))
                  );
                })
              );
            }

            return false;
          });

          if (shouldRun) {
            requestDelayedApply();
          }
        });

        observer.observe(form, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['value', 'disabled', 'selected']
        });
      });
  }

  function bindPageObserver() {
    if (document.body?.dataset.sellingPlanPageObserverBound === 'true') return;

    document.body.dataset.sellingPlanPageObserverBound = 'true';

    const observer = new MutationObserver(function (mutations) {
      const shouldRun = mutations.some(function (mutation) {
        if (mutation.type !== 'childList') return false;

        return Array.from(mutation.addedNodes || []).some(function (node) {
          if (!(node instanceof HTMLElement)) return false;

          return (
            node.matches?.('form[data-type="add-to-cart-form"]') ||
            node.matches?.('input[name="selling_plan"], select[name="selling_plan"]') ||
            node.matches?.('[data-order-now-button]') ||
            node.querySelector?.('form[data-type="add-to-cart-form"]') ||
            node.querySelector?.(
              'input[name="selling_plan"], select[name="selling_plan"], [data-order-now-button]'
            )
          );
        });
      });

      if (shouldRun) {
        bindFormObservers();
        requestDelayedApply();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function stripSellingPlansFromCart() {
    return fetch(shopifyRoute('cart.js'), {
      credentials: 'same-origin'
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        const itemsWithPlan = (cart.items || []).filter(function (item) {
          const planId = String(
            item?.selling_plan_allocation?.selling_plan?.id || ''
          );

          return planId && ALL_SELLING_PLAN_IDS.includes(planId);
        });

        if (itemsWithPlan.length === 0) {
          return Promise.resolve();
        }

        return itemsWithPlan.reduce(function (chain, item) {
          return chain.then(function () {
            return fetch(shopifyRoute('cart/change.js'), {
              method: 'POST',
              credentials: 'same-origin',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                id: item.key,
                quantity: item.quantity,
                selling_plan: ''
              })
            }).then(function (response) {
              return response.json();
            });
          });
        }, Promise.resolve());
      });
  }

  function bindCheckoutButton() {
    if (
      document.documentElement.dataset.sellingPlanCheckoutBound === 'true'
    ) {
      return;
    }

    document.documentElement.dataset.sellingPlanCheckoutBound = 'true';

    document.addEventListener(
      'click',
      function (event) {
        const btn = event.target.closest(
          '#checkout, [name="checkout"], .cart__checkout-button'
        );

        if (!btn) return;
        if (btn.dataset.stripping === 'true') return;

        event.preventDefault();
        event.stopImmediatePropagation();

        btn.dataset.stripping = 'true';
        btn.disabled = true;
        btn.style.opacity = '0.6';

        stripSellingPlansFromCart()
          .then(function () {
            window.location.href = shopifyRoute('checkout');
          })
          .catch(function (error) {
            console.warn(
              '[Selling Plan Overrides] Cart cleanup failed:',
              error
            );
            window.location.href = shopifyRoute('checkout');
          })
          .finally(function () {
            btn.dataset.stripping = 'false';
            btn.disabled = false;
            btn.style.opacity = '';
          });
      },
      true
    );
  }

  function bindOrderNowButton() {
    if (document.documentElement.dataset.orderNowButtonBound === 'true') {
      return;
    }

    document.documentElement.dataset.orderNowButtonBound = 'true';

    document.addEventListener('click', function (event) {
      if (!(event.target instanceof Element)) return;

      const button = event.target.closest('[data-order-now-button]');

      if (!button) return;

      const form = button.closest('form[data-type="add-to-cart-form"]');

      if (!form) return;

      event.preventDefault();
      event.stopPropagation();

      if (!isOrderNowEligible(form)) {
        updateOrderNowButton(form);
        return;
      }

      const variantId = getCurrentVariantId(form);

      if (!variantId) {
        updateOrderNowButton(form);
        return;
      }

      const quantityInput = form.querySelector('input[name="quantity"]');
      const parsedQuantity = Number.parseInt(
        quantityInput?.value || '1',
        10
      );
      const quantity =
        Number.isInteger(parsedQuantity) && parsedQuantity > 0
          ? parsedQuantity
          : 1;

      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      const checkoutPath =
        'cart/' +
        encodeURIComponent(variantId) +
        ':' +
        quantity +
        '?checkout';

      window.location.assign(shopifyRoute(checkoutPath));
    });
  }

  function removeUnusedResponsiveGroup() {
    const breakpoint = 750;
    const isDesktop = window.innerWidth >= breakpoint;

    const mobileGroup = document.querySelector('.group-block.mobile');
    const desktopGroup = document.querySelector('.group-block.desktop');

    if (isDesktop && mobileGroup) {
      mobileGroup.remove();
    } else if (!isDesktop && desktopGroup) {
      desktopGroup.remove();
    }
  }

  function init() {
    applyOverrides();
    bindFormObservers();
    bindPageObserver();
    bindCheckoutButton();
    bindOrderNowButton();
    removeUnusedResponsiveGroup();
  }

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  ready(init);

  document.addEventListener('variant:update', requestDelayedApply);
  document.addEventListener('variant:change', requestDelayedApply);
  document.addEventListener('availability:mode:updated', requestDelayedApply);
  document.addEventListener('shopify:section:load', init);

  document.addEventListener('change', function (event) {
    if (
      event.target.closest('variant-picker') ||
      event.target.closest('form[data-type="add-to-cart-form"]')
    ) {
      requestDelayedApply();
    }
  });

  [0, 50, 150, 300, 600, 1000].forEach(function (delay) {
    setTimeout(requestApplyOverrides, delay);
  });
})();